/**
 * What this page holds for the signed-in account ends with the account.
 *
 * The query cache, the source registry and the events sockets are keyed by
 * server, never by user, and outlive the signed-in tree. Nothing ended them
 * with the session: after Sign out and another account's sign-in in the same
 * tab, the page went on showing the first account's servers — and asking them
 * with the tokens minted for that account — until the next /v1/resources poll
 * a minute later (Kenny, 2026-10-05).
 *
 * One watcher on the session state covers every way an account ends or
 * changes: Sign out, Sign out everywhere, "Use another account", a 401,
 * Delete account, a sign-out or a sign-in in another tab, the server-hosted
 * app's pin sign-in. It acts on both edges, leaving and arriving: a request
 * in flight at the sign-out can still write to the cache after it was
 * cleared, and the next account must start from nothing.
 *
 * Storage is not this module's business. What is kept across reloads for an
 * account carries the user id and is checked on read (lib/ambientArt,
 * lib/playerPrefs) — a session can end while no page is open to hear it.
 */
import type { QueryClient } from "@tanstack/react-query";
import { auth, type AuthState } from "./authManager";

const resets = new Set<() => void>();

/** For a module that keeps something of the account's in memory outside the
 * query cache (sources/registry, lib/activityHub): the reset is registered
 * beside the state it clears. */
export function onAccountEnd(reset: () => void): () => void {
  resets.add(reset);
  return () => {
    resets.delete(reset);
  };
}

/** Pure: whose session a state is — the user's id, null when signed out,
 * undefined while that is not known yet. */
export function accountOf(state: AuthState): string | null | undefined {
  if (state.status === "loading") return undefined;
  return state.status === "signedIn" ? state.user.id : null;
}

interface SessionSource {
  getState(): AuthState;
  subscribe(fn: (state: AuthState) => void): () => void;
}

/**
 * Called once, before the first render (main.tsx). The same account again —
 * a profile edit, the offline flag, a /v1/me re-check — is no change, and
 * neither is the first verdict of a page load: nothing is held yet.
 */
export function watchAccount(qc: QueryClient, source: SessionSource = auth): () => void {
  let account = accountOf(source.getState());
  return source.subscribe((state) => {
    const next = accountOf(state);
    if (next === undefined || next === account) return;
    const first = account === undefined;
    account = next;
    if (first) return;
    qc.clear();
    for (const reset of resets) reset();
  });
}
